import { useEffect, useRef, useState } from "react";
import { type TagVerifyResponse } from "@eagledispatch/shared";
import { apiFetch, ApiError } from "../lib/api";
import { scanTagUid } from "../lib/nfc";
import { useTenantPhone, phoneHref } from "../lib/tenant-phone";
import { TENANT_PHONE } from "../surfaces/driver/constants";
import Button from "./Button";
import Card from "./Card";

/**
 * SPEC-46 rev 6: the gate does NOT punch. On a successful verify it hands the
 * scanned uid to the persistent shell (App.tsx) via onVerified and then opens
 * the console via onPassed. Punching here used to be aborted by this
 * component's own unmount cleanup — the gate unmounts in the same React
 * commit that opens the console, so the in-flight clock-in never left the
 * browser (0/3 INs landed in the field).
 */
export interface DispatchNfcGateProps {
  onVerified: (uid: string) => void;
  onPassed: () => void;
  onBack: () => void;
}

type GateState = "ready" | "scanning" | "verifying" | "error";

export default function DispatchNfcGate({
  onVerified,
  onPassed,
  onBack,
}: DispatchNfcGateProps) {
  const [state, setState] = useState<GateState>("ready");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const phoneState = useTenantPhone();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const startScan = async () => {
    setState("scanning");
    setErrorMessage(null);

    const result = await scanTagUid(30_000);
    if (!mountedRef.current) return;

    if ("error" in result) {
      if (result.error === "cancelled") {
        setState("ready");
        return;
      }
      setState("error");
      if (result.error === "unsupported") {
        setErrorMessage("NFC scanning is not supported on this device/browser.");
      } else if (result.error === "timeout") {
        setErrorMessage("Scan timed out. Please try again.");
      } else {
        setErrorMessage(result.error || "NFC scan failed.");
      }
      return;
    }

    setState("verifying");
    let verifySuccess = false;
    try {
      const res = await apiFetch<TagVerifyResponse>("/api/v1/timeclock/tags/verify", {
        method: "POST",
        body: JSON.stringify({ uid: result.uid }),
      });
      if (!mountedRef.current) return;

      if (res.valid) {
        verifySuccess = true;
      } else {
        setState("error");
        setErrorMessage("Tag not recognized.");
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      setState("error");
      const msg = err instanceof ApiError || err instanceof Error ? err.message : String(err);
      setErrorMessage(msg || "Verification failed. Try again.");
    }

    if (verifySuccess && mountedRef.current) {
      // SPEC-46 rev 6 §2.5 ordering — both calls synchronous, no await
      // between them: hand the uid to App (which launches the clock-in
      // punch fire-and-forget), THEN open the console. The console never
      // waits on the punch, and the gate never learns its outcome.
      onVerified(result.uid);
      onPassed();
    }
  };

  const handleCancelScan = () => {
    setState("ready");
  };

  const renderHelpLine = () => {
    if (phoneState.status === "loading" || phoneState.status === "fallback") {
      return (
        <a href={phoneHref(TENANT_PHONE)} className="contact-link" style={{ fontSize: "13px" }}>
          Having trouble? Call office ({TENANT_PHONE})
        </a>
      );
    }
    if (phoneState.status === "ok") {
      if (phoneState.phone) {
        return (
          <a href={phoneHref(phoneState.phone)} className="contact-link" style={{ fontSize: "13px" }}>
            Having trouble? Call office ({phoneState.phone})
          </a>
        );
      }
      return (
        <span className="muted" style={{ fontSize: "13px" }}>
          Office phone not configured
        </span>
      );
    }
    return null;
  };

  return (
    <main className="surface" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh" }}>
      <div style={{ maxWidth: "420px", width: "100%", textAlign: "center" }}>
        <Card className="login-card">
          <div style={{ padding: "16px 8px" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏷️</div>

            {state === "ready" && (
              <>
                <h1 style={{ fontSize: "22px", marginBottom: "8px" }}>Scan your dispatch tag</h1>
                <p className="muted" style={{ fontSize: "14px", marginBottom: "24px" }}>
                  Hold your registered NFC tag against your phone to access the dispatch console.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <Button variant="primary" onClick={startScan} style={{ width: "100%", minHeight: "52px" }}>
                    Scan tag
                  </Button>
                  <Button variant="ghost" onClick={onBack} style={{ width: "100%" }}>
                    ← Back
                  </Button>
                </div>
              </>
            )}

            {state === "scanning" && (
              <>
                <h1 style={{ fontSize: "22px", marginBottom: "8px" }}>Hold phone near tag</h1>
                <p className="muted" style={{ fontSize: "14px", marginBottom: "24px" }}>
                  Scanning for NFC tag…
                </p>
                <Button variant="secondary" onClick={handleCancelScan} style={{ width: "100%" }}>
                  Cancel
                </Button>
              </>
            )}

            {state === "verifying" && (
              <>
                <h1 style={{ fontSize: "22px", marginBottom: "8px" }}>Checking tag…</h1>
                <p className="muted" style={{ fontSize: "14px" }}>
                  Verifying credentials with dispatch server…
                </p>
              </>
            )}

            {state === "error" && (
              <>
                <h1 style={{ fontSize: "22px", marginBottom: "8px", color: "var(--red)" }}>Verification Failed</h1>
                <div className="banner banner-danger" style={{ marginBottom: "20px", textAlign: "left" }}>
                  {errorMessage || "Tag verification failed."}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
                  <Button variant="primary" onClick={startScan} style={{ width: "100%" }}>
                    Try Again
                  </Button>
                  <Button variant="ghost" onClick={onBack} style={{ width: "100%" }}>
                    ← Back
                  </Button>
                </div>
                <div>{renderHelpLine()}</div>
              </>
            )}
          </div>
        </Card>
      </div>
    </main>
  );
}
